import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { FunctionComponent } from 'react';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import { defaultThemeProp } from '../../../theme';
import type { BaseProps, Action, ForwardProps, TestIdProp } from '../../../types';
import Button from '../../Button';
import { useArrows, useConfiguration, useElement } from '../../../hooks';
import { is12HClockFormat } from '../Input/utils';
import type { ClockFormat } from '../Input/utils';
import { tryCatch } from '../../../utils';
import { getIntlDateTimeFormatFromCache } from '../utils';

import { getIntervalGenerator } from './utils';
import type { MinuteInterval } from './DatePicker.types';

export interface TimePickerProps extends BaseProps, TestIdProp {
  /**
   * Defines default time that will be selected when the Time Picker is opened.
   * @default new Date()
   */
  selected?: Date;
  /** Callback fired when selected value changes. */
  onSelect: (date: Date) => void;
  /**
   * Defines interval for time picker options (in minutes).
   * @default 30
   */
  interval?: MinuteInterval;
  /** Defines clock format that overrides locale based format */
  clockFormat?: ClockFormat;
  /** Display seconds in picker option labels. */
  withSeconds?: boolean;
}

const StyledTimePicker = styled.ul`
  list-style: none;
  max-height: 10rem;
  overflow: auto;
  width: 100%;
`;

StyledTimePicker.defaultProps = defaultThemeProp;

const StyledTimeButton = styled.button<TimePickerProps>(
  ({ theme: { base, components }, selected }) => {
    const activeColor = tryCatch(() =>
      mix(0.85, base.palette['primary-background'], base.palette.interactive)
    );

    const { spacing } = base;

    return css`
      color: ${base.palette['foreground-color']};
      width: 100%;
      border-radius: inherit;
      overflow: hidden;
      text-align: start;
      text-overflow: ellipsis;
      white-space: nowrap;
      border-width: 0;
      justify-content: start;
      padding: 0;
      background-color: ${selected ? activeColor : 'transparent'};

      > span {
        margin-inline-end: calc(0.25 * ${spacing});
        &:first-child {
          margin-inline-start: ${components['form-control']['border-width']};
          padding-inline-start: ${spacing};
        }
        &:nth-child(2),
        &:nth-child(3) {
          margin-inline-start: calc(0.25 * ${spacing});
        }
        &[data-meridiem] {
          padding-inline-start: calc(0.75 * ${spacing});
        }
      }

      &:enabled:focus,
      &:not([disabled]):focus {
        box-shadow: ${base.shadow['focus-inset']};
      }
    `;
  }
);

StyledTimeButton.defaultProps = defaultThemeProp;

const StyledTimeItem = styled.li`
  border-radius: 0;
`;

StyledTimeItem.defaultProps = defaultThemeProp;

const TimePicker: FunctionComponent<TimePickerProps & ForwardProps> = (props: TimePickerProps) => {
  const { locale } = useConfiguration();
  const {
    testId,
    selected = new Date(0),
    interval = 30,
    clockFormat,
    withSeconds,
    onSelect,
    ...restProps
  } = props;

  const intervalInMS = interval * 60000;

  const { hourCycle: defaultHourCycle } = useMemo(
    () =>
      getIntlDateTimeFormatFromCache(locale, {
        hour: '2-digit',
        hour12: clockFormat ? clockFormat === 12 : is12HClockFormat(locale)
      }).resolvedOptions(),
    [clockFormat, locale]
  );

  const actionsList = Array.from(getIntervalGenerator(intervalInMS), time => {
    const date = selected ? new Date(new Date().setUTCHours(0, 0, 0, 0)) : new Date(selected);
    date.setUTCHours(time.getUTCHours(), time.getUTCMinutes(), time.getUTCSeconds());
    return {
      id: date.getTime().toString(),
      text: Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        ...(withSeconds && { second: '2-digit' }),
        timeZone: 'UTC',
        /** Use this to fix Chrome https://bugs.chromium.org/p/chromium/issues/detail?id=1045791 */
        hourCycle: defaultHourCycle === 'h24' ? 'h23' : defaultHourCycle
      }).format(date)
    };
  });

  const isSelected = (date: Date) => {
    return (
      selected?.getUTCHours() === date.getUTCHours() &&
      selected?.getUTCMinutes() === date.getUTCMinutes() &&
      (!withSeconds || selected?.getUTCSeconds() === date.getUTCSeconds())
    );
  };

  const [buttonEl, setButtonEl] = useElement<HTMLElement>();
  const menuRef = useRef<HTMLUListElement>(null);

  useArrows(menuRef, {
    selector: 'button',
    cycle: true,
    dir: 'up-down',
    allowTabFocus: false
  });

  const renderTime = (text: string) => {
    const parts = text.split(/:| /);
    const hour = parts[0];
    const minute = parts[1];
    const second = withSeconds ? parts[2] : undefined;
    const meridiem = withSeconds ? parts[3] : parts[2];
    return (
      <>
        <span>{hour}</span>:<span>{minute}</span>
        {second !== undefined && (
          <>
            :<span>{second}</span>
          </>
        )}
        {meridiem && <span data-meridiem>{meridiem}</span>}
      </>
    );
  };

  const setTabIndexes = useCallback(() => {
    const focusEl = menuRef.current;
    if (!focusEl) return;
    const focusableElements = Array.from(focusEl.querySelectorAll('button'));

    if (focusableElements.length > 0) {
      let isChecked = false;
      focusableElements.slice(1).forEach(item => {
        const checked = (item as HTMLInputElement).checked;
        if (checked) {
          isChecked = true;
          item.tabIndex = 0;
        } else item.tabIndex = -1;
      });

      if (!isChecked) {
        (focusableElements[0] as HTMLElement).tabIndex = 0;
        focusableElements[0].focus();
      }
    }
  }, [selected]);

  useEffect(() => {
    buttonEl?.focus();
    setTabIndexes();
  }, [buttonEl]);

  return (
    <StyledTimePicker data-testid={testId} role='menu' tabIndex={-1} ref={menuRef} {...restProps}>
      {actionsList.map(({ text, id }: Action) => {
        const item = new Date(Number(id));
        return (
          <StyledTimeItem role='presentation' key={id}>
            <StyledTimeButton
              role='menuitem'
              as={Button}
              variant='simple'
              ref={isSelected(item) ? setButtonEl : null}
              aria-label={text}
              selected={isSelected(item)}
              aria-current={isSelected(item) ? 'time' : undefined}
              checked={isSelected(item)}
              onClick={() => {
                onSelect?.(item);
              }}
              tabIndex='-1'
            >
              {renderTime(text)}
            </StyledTimeButton>
          </StyledTimeItem>
        );
      })}
    </StyledTimePicker>
  );
};

export default TimePicker;
